import React, { useEffect, useState } from "react";
import CustomHeader from "../common/Header";
import { Button, Card, Skeleton } from "antd";
import {
    ApartmentOutlined,
    EditOutlined,
    MailOutlined,
    PlusOutlined,
    SolutionOutlined,
} from "@ant-design/icons";
import AddEditForm from "./Form";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import CustomTable from "../common/Table";
import "./Contacts.css";

function Contacts() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [addEditModalVisible, setAddEditModalVisible] = useState(0);
    const [editRecord, setEditRecord] = useState({});
    const [tableData, setTableData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const [formData, setFormData] = useState({
        page: 0,
        limit: 10,
        sorting: 1,
        sort_by: "updated_at",
    });

    const {
        status: contactStatus,
        data: contactData,
        refetch: getContactDetails,
    } = useQuery(
        "back-office/contacts",
        async () =>
            HTTP("/backOffice/getBackOfficeContacts", {
                ...formData,
                staff_id,
            }),
        {
            enabled: false,
        },
    );

    useEffect(() => {
        getContactDetails();
    }, []);

    useEffect(() => {
        getContactDetails();
    }, [formData]);

    useEffect(() => {
        if (contactStatus == "success") {
            setTableData(contactData.data);
            setTotalRecords(contactData.total);
        }
    }, [contactStatus, contactData]);

    function handleAdd() {
        setAddEditModalVisible(1);
    }

    function handleEdit(record) {
        setEditRecord(record);
        setAddEditModalVisible(2);
    }

    function handleCancel(reload) {
        setAddEditModalVisible(0);
        reload && getContactDetails();
    }

    const neutralActionButtonStyle = {
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: 7,
        width: 30,
        height: 30,
    };

    const renderEmpty = (label = "Not available") => (
        <span className="contacts-empty">{label}</span>
    );

    const renderIdentityCell = (title, subtitle) => (
        <div className="contacts-identity">
            <div className="contacts-identity-icon">
                <ApartmentOutlined />
            </div>
            <div className="contacts-identity-copy">
                <span className="contacts-identity-title">
                    {title || "Property not added"}
                </span>
                <span className="contacts-identity-subtitle">
                    {subtitle || "Company not added"}
                </span>
            </div>
        </div>
    );

    const renderPlainText = (value, emptyLabel) =>
        value ? <span className="contacts-text">{value}</span> : renderEmpty(emptyLabel);

    const renderChip = (label, type = "primary", emptyLabel = "Not assigned") =>
        label ? (
            <span className={`contacts-chip contacts-chip-${type}`}>{label}</span>
        ) : (
            renderEmpty(emptyLabel)
        );

    const renderContactList = (data, emptyLabel, type = "primary") =>
        Array.isArray(data) && data.length > 0 ? (
            <div className="contacts-list">
                {data.map(({ first_name, last_name, number, email }, index) => {
                    const title = `${first_name || ""} ${last_name || ""}`.trim();
                    const detail = number || email;
                    return (
                        <div className="contacts-list-item" key={`${detail || "item"}-${index}`}>
                            <span
                                className={`contacts-chip contacts-chip-${type}`}
                            >
                                <SolutionOutlined />
                                {title || "Unnamed contact"}
                            </span>
                            {detail ? (
                                <span className="contacts-list-detail">{detail}</span>
                            ) : (
                                renderEmpty("Number not added")
                            )}
                        </div>
                    );
                })}
            </div>
        ) : (
            renderEmpty(emptyLabel)
        );

    const columns = [
        {
            title: "Property Name",
            dataIndex: "property_name",
            key: "property_name",
            sorter: () => {},
            width: 220,
            render: (_, record) =>
                renderIdentityCell(record.property_name, record.company_name),
        },
        {
            title: "Company",
            dataIndex: "company_name",
            key: "company_name",
            sorter: () => {},
            width: 160,
            render: (value) => renderChip(value, "neutral", "Company not added"),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            sorter: () => {},
            width: 220,
            render: (value) =>
                value ? (
                    <div className="contacts-contact">
                        <MailOutlined className="contacts-contact-icon" />
                        <span>{value}</span>
                    </div>
                ) : (
                    renderEmpty("Email not added")
                ),
        },
        {
            title: "Fax Number",
            dataIndex: "fax_number",
            key: "fax_number",
            sorter: () => {},
            width: 150,
            render: (value) => renderPlainText(value, "Fax not added"),
        },
        {
            title: "Phone",
            dataIndex: "phone_number",
            key: "phone_number",
            width: 220,
            render: (data) => renderContactList(data, "No owner contact", "primary"),
        },
        {
            title: "Bussiness Phone",
            dataIndex: "business_phone_number",
            key: "business_phone_number",
            width: 220,
            render: (data) =>
                renderContactList(data, "No business contact", "accent"),
        },
        {
            title: "Account Phone",
            dataIndex: "account_phone_number",
            key: "account_phone_number",
            width: 220,
            render: (data) =>
                renderContactList(data, "No account contact", "neutral"),
        },
        {
            title: "Mobile Phone",
            dataIndex: "mobile_number",
            key: "mobile_number",
            width: 220,
            render: (data) =>
                renderContactList(data, "No hotel contact", "primary"),
        },
        {
            title: "Notes",
            dataIndex: "notes",
            key: "notes",
            sorter: () => {},
            width: 180,
            render: (value) => renderPlainText(value, "No notes added"),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Button
                    type="text"
                    size="small"
                    icon={
                        <EditOutlined
                            style={{ color: "var(--color-primary)" }}
                        />
                    }
                    onClick={() => handleEdit(record)}
                    style={neutralActionButtonStyle}
                />
            ),
            width: 90,
        },
    ];

    function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        } else {
            delete formData.sorting;
            delete formData.sort_by;
        }

        const newFormData = {
            ...formData,
            ...others,
        };

        setFormData(newFormData);
    }

    return (
        <>
            <Card className="dashboard-container">
                <CustomHeader
                    extraAfter={
                        <div>
                            <Button
                                type="primary"
                                style={{ marginRight: "5px" }}
                                onClick={handleAdd}
                            >
                                <PlusOutlined /> Add
                            </Button>
                        </div>
                    }
                />
                {contactStatus !== "idle" && contactStatus !== "loading" ? (
                    <CustomTable
                        handleTableChange={handleTableChange}
                        columns={columns}
                        tableData={tableData}
                        noOfrecords={totalRecords}
                        rowKey="id"
                    />
                ) : (
                    <Skeleton active title={false} paragraph={{ rows: 40 }} />
                )}
            </Card>

            {addEditModalVisible == 0 ? (
                <></>
            ) : addEditModalVisible == 1 ? (
                <AddEditForm handleCancel={handleCancel} />
            ) : (
                <AddEditForm
                    handleCancel={handleCancel}
                    editRecord={editRecord}
                />
            )}
        </>
    );
}

export { Contacts };
